import type { InterceptedError } from '@/shared/api/intercepted-error';
import {
  useQuery,
  useQueryClient,
  type QueryMeta,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { invalidateStorageQueries } from './invalidate-storage-queries';

export type CacheStorageOptions<T = unknown, TData = T> = UseQueryOptions<
  T,
  InterceptedError,
  TData
> & {
  meta: QueryMeta & { cacheStorageEvent: string };
};

export const useCacheStorage = <T, TData = T>({
  queryConfig,
}: {
  queryConfig: CacheStorageOptions<T, TData>;
}) => useQuery(queryConfig);

export const useInvalidateCacheStorage = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (queryConfig: UseQueryOptions) => {
      const { queryHash } = queryClient.defaultQueryOptions(queryConfig);
      const query = queryClient.getQueryCache().get(queryHash);
      await invalidateStorageQueries(queryClient, {
        queryKey: queryConfig.queryKey,
      });

      // Explicit refreshes also warm storages with no enabled observers.
      if (!query || query.isDisabled()) {
        await queryClient.prefetchQuery({ ...queryConfig, staleTime: 0 });
      }
    },
    [queryClient],
  );
};